import React from 'react';
import { Outlet } from 'react-router-dom';
import logo from '../../assets/saung-logo.svg';

/**
 * AuthLayout Component
 * 
 * Layout wrapper for authentication pages (login, register, etc.)
 * with a beautiful gradient background and centered card design.
 * 
 * Features:
 * - Responsive design
 * - Beautiful gradient background
 * - Centered card layout
 * - Animated background
 */

export interface AuthLayoutProps {
  children?: React.ReactNode;
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  console.log('AuthLayout rendered', { children });
  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden">
      {/* Animated Gradient Background — SAUNG dark theme */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B0B0F] via-[#121218] to-[#713F12] animate-gradient-shift">
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0F]/50 via-transparent to-transparent" />
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-[#EAB308]/20 rounded-full blur-3xl animate-float-slow" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#CA8A04]/15 rounded-full blur-3xl animate-float-slower" />
          <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-[#FACC15]/15 rounded-full blur-3xl animate-float-slow" />
        </div>
      </div>
      
      {/* Content Container */}
      <div className="relative z-10 w-full max-w-md mx-auto px-4">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl backdrop-blur-sm pt-8 px-6 pb-8">
          {/* Card Header with Blue Gradient - Animated, Positioned with TranslateY */}
          {/* <div className="h-10">
          </div> */}
          <div className="absolute top-0 bg-gradient-to-r from-[#713F12] via-[#EAB308] to-[#CA8A04] rounded-xl shadow-lg text-center animate-slide-up mx-auto w-[90%] px-8 py-10">
            <div className="flex items-center justify-center">
              <img 
                src={logo} 
                alt="SAUNG Logo" 
                className="h-20 w-auto object-contain"
              />
            </div>
            
            {/* Social Icons */}
            {/* <div className="flex justify-center gap-4 mt-6">
              <button
                type="button"
                className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-[#FFD700]/30 hover:scale-110 transition-all duration-200"
                aria-label="Sign in with Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </button>
              
              <button
                type="button"
                className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-[#FFD700]/30 hover:scale-110 transition-all duration-200"
                aria-label="Sign in with GitHub"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                </svg>
              </button>
              
              <button
                type="button"
                className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-[#FFD700]/30 hover:scale-110 transition-all duration-200"
                aria-label="Sign in with Google"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              </button>
            </div> */}
          </div>
          
          {/* Card Body */}
          <div className="px-4 py-8 mt-14">
            {/* <div className="text-center mb-4">
              <h1 className="text-2xl font-bold text-gray-900">Debug Test</h1>
              <p className="text-gray-600">If you can see this, the layout is working!</p>
            </div> */}
            {children || <Outlet />}
          </div>
        </div>
        
        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-white text-sm drop-shadow-lg">
            © {new Date().getFullYear()} SAUNG. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;

